import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Phone,
  MessageSquare,
  Mail,
  Trash2,
  Flame,
  XCircle,
  CalendarClock,
  Sparkles,
  FileText,
  ArrowRight,
  ListChecks,
  Route,
  Maximize2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { leadsApi } from "@/lib/crmApi";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import StarScore from "@/components/crm/StarScore";
import ProcedureChecklist from "@/components/crm/ProcedureChecklist";
import Programme20j from "@/components/crm/Programme20j";
import {
  STATUTS,
  PRIORITES,
  LOCALISATIONS,
  TYPE_APPARTS,
  HORIZONS,
  formatDateTime,
  getInitials,
  statutColor,
  statutLabel,
  BUCKET_META,
  bucketOf,
} from "@/lib/crmHelpers";

const ICONS = {
  appel: Phone,
  sms: MessageSquare,
  email: Mail,
  note: FileText,
  statut: ArrowRight,
  priorite: Flame,
  relance: CalendarClock,
  creation: Sparkles,
  procedure: ListChecks,
  programme: Route,
};

export default function LeadDetailDialog({
  lead,
  onOpenChange,
  onUpdate,
  onDelete,
  onAction,
  onProcedureToggle,
  onProgrammeToggle,
}) {
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (lead) {
      setForm({
        nom: lead.nom,
        telephone: lead.telephone,
        email: lead.email || "",
        statut: lead.statut,
        priorite: lead.priorite,
        score: lead.score || 0,
        localisation: lead.localisation || "dakar",
        type_appart: lead.type_appart || "autre",
        horizon: lead.horizon || "inconnu",
        budget: lead.budget || "",
        programme_immobilier: lead.programme_immobilier || "",
        commercial: lead.commercial || "",
        source: lead.source || "",
        prochaine_action: lead.prochaine_action?.slice(0, 10) || "",
        notes: lead.notes || "",
      });
    }
  }, [lead]);

  if (!lead || !form) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const bMeta = BUCKET_META[bucketOf(lead)];

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(lead.id, {
        ...form,
        prochaine_action: form.prochaine_action || null,
      });
      toast.success("Lead mis à jour");
    } catch (e) {
      toast.error("Erreur de sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const quickAction = async (type, desc) => {
    try {
      await onAction(lead.id, type, desc);
      toast.success("Action enregistrée");
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const handleCall = () => {
    // Open phone app only — user logs manually after the call
    window.location.href = `tel:${lead.telephone.replace(/\s+/g, "")}`;
  };

  const handleSMS = () => {
    // Open SMS app only — no auto-log
    window.location.href = `sms:${lead.telephone.replace(/\s+/g, "")}`;
  };

  const handleEmail = () => {
    if (!lead.email) {
      toast.error("Aucun email renseigné");
      return;
    }
    // Open mail client only — no auto-log
    window.location.href = `mailto:${lead.email}`;
  };

  const markHot = async () => {
    await onUpdate(lead.id, { priorite: "haute", score: 4 });
    toast.success("Marqué comme HOT");
  };

  const markLost = async () => {
    await onUpdate(lead.id, { statut: "perdu" });
    toast.success("Marqué comme perdu");
  };

  const history = [...(lead.historique || [])].sort((a, b) =>
    b.date.localeCompare(a.date)
  );

  return (
    <Dialog open={!!lead} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[92vh] overflow-y-auto sm:max-w-[820px]"
        data-testid="lead-detail-dialog"
      >
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-muted font-heading text-base font-semibold">
                {getInitials(lead.nom)}
              </div>
              <div className="min-w-0">
                <DialogTitle className="font-heading truncate text-xl">
                  {lead.nom}
                </DialogTitle>
                <DialogDescription className="flex flex-wrap items-center gap-1.5 pt-1">
                  <span className="text-sm">{lead.telephone}</span>
                  {lead.email && <span className="text-sm">· {lead.email}</span>}
                </DialogDescription>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <span
                    className={`rounded-md px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ${statutColor(
                      lead.statut
                    )}`}
                  >
                    {statutLabel(lead.statut)}
                  </span>
                  <span
                    className={`rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${bMeta.color}`}
                  >
                    {bMeta.label}
                  </span>
                  <StarScore value={lead.score || 0} readOnly size="sm" testId={`detail-stars-${lead.id}`} />
                </div>
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Quick actions */}
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleCall}
            data-testid="detail-call-btn"
            className="gap-1.5"
          >
            <Phone className="h-3.5 w-3.5" />
            Appeler
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleSMS}
            data-testid="detail-sms-btn"
            className="gap-1.5"
          >
            <MessageSquare className="h-3.5 w-3.5" />
            SMS
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleEmail}
            data-testid="detail-email-btn"
            className="gap-1.5"
          >
            <Mail className="h-3.5 w-3.5" />
            Email
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={markHot}
            data-testid="detail-hot-btn"
            className="gap-1.5"
          >
            <Flame className="h-3.5 w-3.5 text-red-500" />
            Marquer HOT
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={markLost}
            data-testid="detail-lost-btn"
            className="gap-1.5"
          >
            <XCircle className="h-3.5 w-3.5" />
            Perdu
          </Button>
          <div className="flex-1" />
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              navigate(`/leads/${lead.id}`);
            }}
            data-testid="detail-fullscreen-btn"
            className="gap-1.5"
          >
            <Maximize2 className="h-3.5 w-3.5" />
            Fiche complète
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              if (window.confirm("Supprimer ce lead ?")) onDelete(lead.id);
            }}
            data-testid="detail-delete-btn"
            className="gap-1.5 text-muted-foreground hover:text-red-500"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>

        <Separator />

        <Tabs defaultValue="infos" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="infos" data-testid="tab-infos">Infos</TabsTrigger>
            <TabsTrigger value="procedure" data-testid="tab-procedure">Procédure</TabsTrigger>
            <TabsTrigger value="programme" data-testid="tab-programme">Programme 20j</TabsTrigger>
            <TabsTrigger value="historique" data-testid="tab-historique">Historique</TabsTrigger>
          </TabsList>

          <TabsContent value="infos" className="space-y-4 pt-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Nom</Label>
                <Input
                  data-testid="detail-edit-nom"
                  value={form.nom}
                  onChange={(e) => set("nom", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Téléphone</Label>
                <Input
                  data-testid="detail-edit-telephone"
                  value={form.telephone}
                  onChange={(e) => set("telephone", e.target.value)}
                />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label>Email</Label>
                <Input
                  data-testid="detail-edit-email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Statut</Label>
                <Select
                  value={form.statut}
                  onValueChange={(v) => set("statut", v)}
                >
                  <SelectTrigger data-testid="detail-edit-statut">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUTS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Priorité</Label>
                <Select
                  value={form.priorite}
                  onValueChange={(v) => set("priorite", v)}
                >
                  <SelectTrigger data-testid="detail-edit-priorite">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Score</Label>
                <div className="flex h-10 items-center rounded-md border border-input px-3">
                  <StarScore
                    value={form.score}
                    onChange={(v) => set("score", v)}
                    size="md"
                    testId="detail-edit-score"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Localisation</Label>
                <Select
                  value={form.localisation}
                  onValueChange={(v) => set("localisation", v)}
                >
                  <SelectTrigger data-testid="detail-edit-localisation">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCALISATIONS.map((l) => (
                      <SelectItem key={l.value} value={l.value}>
                        {l.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Type d'appartement</Label>
                <Select
                  value={form.type_appart}
                  onValueChange={(v) => set("type_appart", v)}
                >
                  <SelectTrigger data-testid="detail-edit-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPE_APPARTS.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Horizon d'achat</Label>
                <Select
                  value={form.horizon}
                  onValueChange={(v) => set("horizon", v)}
                >
                  <SelectTrigger data-testid="detail-edit-horizon">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HORIZONS.map((h) => (
                      <SelectItem key={h.value} value={h.value}>
                        {h.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Budget</Label>
                <Input
                  data-testid="detail-edit-budget"
                  value={form.budget}
                  onChange={(e) => set("budget", e.target.value)}
                  placeholder="ex: 60M - 80M FCFA"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Programme préféré</Label>
                <Input
                  data-testid="detail-edit-programme-immo"
                  value={form.programme_immobilier}
                  onChange={(e) => set("programme_immobilier", e.target.value)}
                  placeholder="ex: RYAD Almadies"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Commercial assigné</Label>
                <Input
                  data-testid="detail-edit-commercial"
                  value={form.commercial}
                  onChange={(e) => set("commercial", e.target.value)}
                  placeholder="ex: Moussa Ndiaye"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Source</Label>
                <Input
                  data-testid="detail-edit-source"
                  value={form.source}
                  onChange={(e) => set("source", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Prochaine action</Label>
                <Input
                  type="date"
                  data-testid="detail-edit-date"
                  value={form.prochaine_action}
                  onChange={(e) => set("prochaine_action", e.target.value)}
                />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label>Notes</Label>
                <Textarea
                  data-testid="detail-edit-notes"
                  value={form.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  rows={3}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Fermer
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                data-testid="detail-save-btn"
              >
                {saving ? "Sauvegarde..." : "Enregistrer"}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="procedure" className="pt-4">
            <ProcedureChecklist
              procedure={lead.procedure || {}}
              onToggle={(key, value) => onProcedureToggle(lead.id, key, value)}
            />
          </TabsContent>

          <TabsContent value="programme" className="pt-4">
            <Programme20j
              programme={lead.programme || []}
              onToggle={(jour, index, fait) =>
                onProgrammeToggle(lead.id, jour, index, fait)
              }
            />
          </TabsContent>

          <TabsContent value="historique" className="pt-4">
            <div className="text-label mb-3 text-muted-foreground">
              Historique des interactions
            </div>
            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Aucune action pour le moment.
              </p>
            ) : (
              <ol className="relative space-y-3 border-l border-border/60 pl-5">
                {history.map((h) => {
                  const Icon = ICONS[h.type] || FileText;
                  return (
                    <li key={h.id} className="group relative">
                      <span className="absolute -left-[27px] top-0.5 flex h-4 w-4 items-center justify-center rounded-full border border-border bg-background">
                        <Icon className="h-2.5 w-2.5 text-muted-foreground" />
                      </span>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm">{h.description}</div>
                          <div className="text-xs text-muted-foreground">
                            {formatDateTime(h.date)}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            if (!window.confirm("Supprimer cette entrée d'historique ?")) return;
                            try {
                              await leadsApi.deleteHistory(lead.id, h.id);
                              if (onUpdate) onUpdate(lead.id, {});
                              toast.success("Entrée supprimée");
                            } catch (e) {
                              toast.error("Erreur lors de la suppression");
                            }
                          }}
                          data-testid={`history-delete-${h.id}`}
                          title="Supprimer cette entrée"
                          className="h-6 w-6 flex-none p-0 text-muted-foreground opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
